import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Upload, Loader2, Sparkles, Database } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminSearchResumes, adminResumeFacets, adminReindexResume, adminSetResumeHired } from '../../api';
import UploadResumeModal from './UploadResumeModal';
import ResumeFilters, { EMPTY_FILTERS } from './ResumeFilters';
import ResumeResultCard from './ResumeResultCard';

const toPayload = (q, match, smart, f, showHired) => ({
  q, match, smart, hired: showHired ? 'only' : 'exclude',
  source: f.source, job_id: f.job_id, min_clearance: f.min_clearance, min_education: f.min_education,
  certifications: f.certifications, location: f.location || null,
  min_years: f.min_years === '' ? null : Number(f.min_years), max_years: f.max_years === '' ? null : Number(f.max_years),
  date_from: f.date_from ? new Date(`${f.date_from}T00:00:00`).toISOString() : null, date_to: f.date_to ? new Date(`${f.date_to}T23:59:59.999`).toISOString() : null,
  limit: 200,
});

export const ResumeRepositoryTab = () => {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [match, setMatch] = useState('all');
  const [smart, setSmart] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [showHired, setShowHired] = useState(false);
  const qRef = useRef(q);
  qRef.current = q;

  const loadFacets = useCallback(() => adminResumeFacets().then(setFacets).catch(() => {}), []);
  const run = useCallback(async (query = qRef.current) => {
    setLoading(true);
    try { setData(await adminSearchResumes(toPayload(query.trim(), match, smart, filters, showHired))); }
    catch (err) { toast({ title: 'Search failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [match, smart, filters, showHired, toast]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { const t = setTimeout(() => run(), 350); return () => clearTimeout(t); }, [run]);

  const processing = (facets?.queued || 0) + (facets?.status?.processing || 0) + (facets?.status?.queued || 0);
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(() => { loadFacets(); run(); }, 6000);
    return () => clearInterval(t);
  }, [processing, loadFacets, run]);

  const total = Object.values(facets?.status || {}).reduce((a, b) => a + b, 0) - (facets?.hired || 0);

  const setHired = async (r, hired) => {
    try {
      await adminSetResumeHired(r.id, hired);
      toast({ title: hired ? `${r.candidate_name} marked as hired` : `${r.candidate_name} moved back to the repository`, description: hired ? 'Hidden from the repository. Find them under "Show hired".' : undefined });
      loadFacets(); run();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  return (
    <div data-testid="resume-repository-tab" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white inline-flex items-center gap-2"><Database className="w-5 h-5 text-cyan-400" />Resume Repository</h3>
          <p data-testid="resume-repo-count" className="text-sm text-slate-400 mt-1">
            {total} resume{total === 1 ? '' : 's'} on file, from website applications and imports.{facets?.hired ? ` ${facets.hired} hired (hidden).` : ''}
            {processing > 0 && <span className="text-cyan-300 ml-1"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Reading {processing} new resume{processing === 1 ? '' : 's'}...</span>}
          </p>
        </div>
        <Button data-testid="resume-import-btn" onClick={() => setImportOpen(true)} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold"><Upload className="w-4 h-4 mr-1" />Import resumes</Button>
      </div>

      <form onSubmit={e => { e.preventDefault(); run(); }} className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="resume-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder='Keywords, e.g. RMF eMASS "Active Directory"' className="pl-9 bg-[#080d18] border-slate-700 text-white h-11" />
          </div>
          <Button data-testid="resume-search-btn" type="submit" className="h-11 px-6 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}</Button>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
          <div className="inline-flex rounded-md border border-slate-700 overflow-hidden">
            {[['all', 'All words'], ['any', 'Any word']].map(([v, l]) => (
              <button key={v} type="button" data-testid={`resume-match-${v}`} onClick={() => setMatch(v)} className={`px-3 py-1.5 transition-colors ${match === v ? 'bg-cyan-400 text-slate-900 font-semibold' : 'hover:bg-slate-800'}`}>{l}</button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <Switch data-testid="resume-smart-switch" checked={smart} onCheckedChange={setSmart} className="data-[state=checked]:bg-cyan-400 data-[state=unchecked]:bg-slate-700" />
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />AI smart search <span className="text-slate-500">(also finds related terms)</span>
          </label>
          <span className="text-slate-500">Use quotes for exact phrases.</span>
        </div>
        {data?.expanded_terms && (
          <p data-testid="resume-expanded-terms" className="text-xs text-slate-400">Also searched: {Object.values(data.expanded_terms).flatMap(v => v.slice(1)).join(', ') || 'no related terms found'}</p>
        )}
      </form>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <ResumeFilters filters={filters} setFilters={setFilters} facets={facets} />
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p data-testid="resume-results-count" className="text-sm text-slate-400">{data ? `${data.total} ${showHired ? 'hired ' : ''}result${data.total === 1 ? '' : 's'}` : 'Searching...'}</p>
            <Button data-testid="resume-show-hired-btn" variant="outline" onClick={() => setShowHired(v => !v)} className={`h-8 text-xs bg-transparent hover:bg-slate-800 ${showHired ? 'border-emerald-500/60 text-emerald-300 hover:text-emerald-200' : 'border-slate-700 text-slate-300 hover:text-white'}`}>
              {showHired ? 'Back to active resumes' : `Show hired (${facets?.hired || 0})`}
            </Button>
          </div>
          {data?.results?.map(r => <ResumeResultCard key={r.id} r={r} onHired={setHired} onReindex={async id => { await adminReindexResume(id); loadFacets(); run(); }} />)}
          {data && data.total === 0 && <p data-testid="resume-results-empty" className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-700 p-8 text-center">No resumes match. Try fewer filters, "Any word", or AI smart search.</p>}
        </div>
      </div>

      <UploadResumeModal open={importOpen} onOpenChange={setImportOpen} onUploaded={() => { loadFacets(); setTimeout(() => { loadFacets(); run(); }, 1500); }} />
    </div>
  );
};

export default ResumeRepositoryTab;
